/**
 * @file index.tsx
 *
 * @fileoverview A component that defines spacing on top or bottom.
 */

import * as React from "react";
import {
  excludeSpacingProps,
  generateMarginClasses,
  generatePaddingClasses,
  SpaceProps,
} from "../../../../theme/spacing";

type SpacingProps = SpaceProps & {
  /**
   * The size of the spacing.
   *
   * @default "small"
   */
  size?: "small" | "medium" | "large";
  /**
   * The content that goes inside the spacing.
   *
   * @default null
   */
  children?: React.ReactNode;
};

const spacesMap = {
  small: 4,
  medium: 6,
  large: 8,
};

/**
 * The Spacing component can be used to wrap some content with either vertical or bottom spacing. or both. it will generate consistent amounts
 * of spacing coming from what is defined in the theme. This ensures that when making space between elements we are defining a consistent look and feel.
 */
export const Spacing = ({
  size = "small",
  children,
  ...props
}: SpacingProps) => {
  // Calculate based on size the right value for margin.
  let sizesProps = {
    ...(props.margin === undefined &&
      props.marginTop === undefined && { marginTop: spacesMap[size] }),
    ...(props.margin === undefined &&
      props.marginBottom === undefined && { marginBottom: spacesMap[size] }),
  };

  return (
    <div
      className={`
        flex-item-element
        ${generateMarginClasses(sizesProps)}
        ${generateMarginClasses(props)}
        ${generatePaddingClasses(props)}
      `.trim()}
      {...excludeSpacingProps(props)}
    >
      {children}
    </div>
  );
};

export default Spacing;
